import { useCallback, useEffect, useRef, useState } from 'react';
import { get } from '../../utils/fetch';

export type IntegrationMessageLevel = 'info' | 'warning' | 'error';

export type IntegrationMessage = {
  id: number;
  subject: string;
  message?: string;
  level: IntegrationMessageLevel;
  created_at: string;
  integration_id: number;
  connector_code?: string;
  connector_name?: string;
  project_id?: number;
  project_name?: string;
  task_id?: number;
  task_local_id?: number;
  user_name?: string;
};

const useIntegrationMessagesEndpoint = (endpoint: string) => {
  const [messages, setMessages] = useState<IntegrationMessage[]>([]);
  const [loading, setLoading] = useState(!!endpoint);
  const [error, setError] = useState('');
  const [refreshToken, setRefreshToken] = useState(0);
  const activeEndpoint = useRef(endpoint);

  useEffect(() => {
    const scopeChanged = activeEndpoint.current !== endpoint;
    activeEndpoint.current = endpoint;

    if (scopeChanged) setMessages([]);
    setError('');

    if (!endpoint) {
      setLoading(false);
      return;
    }

    let active = true;

    if (scopeChanged || refreshToken === 0) setLoading(true);

    get(endpoint)
      .then((response: IntegrationMessage[]) => {
        if (!active) return;

        setMessages(response);
        setLoading(false);
      })
      .catch(requestError => {
        if (!active) return;

        setError(requestError.message || String(requestError));
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [endpoint, refreshToken]);

  const refresh = useCallback(() => {
    setRefreshToken(token => token + 1);
  }, []);

  return { messages, loading, error, refresh };
};

export const useIntegrationMessages = (
  baseUrl: string,
  projectId?: string | number,
  taskId?: number
) =>
  useIntegrationMessagesEndpoint(
    projectId
      ? `${baseUrl}/projects/${projectId}/integration_messages${
          taskId ? `?task_id=${taskId}` : ''
        }`
      : ''
  );

export const useOrganizationIntegrationMessages = (
  baseUrl: string,
  organizationId?: string | number,
  integrationId?: string | number
) =>
  useIntegrationMessagesEndpoint(
    organizationId && integrationId
      ? `${baseUrl}/organizations/${organizationId}/integrations/${integrationId}/integration_messages`
      : ''
  );
